import { useState, useEffect } from 'react';
import enRes from '../i18n/en.json';
import zhTWRes from '../i18n/zh-TW.json';
import zhCNRes from '../i18n/zh-CN.json';
import daRes from '../i18n/da.json';
import deRes from '../i18n/de.json';
import esRes from '../i18n/es.json';
import frRes from '../i18n/fr.json';
import itRes from '../i18n/it.json';
import nlRes from '../i18n/nl.json';
import ptRes from '../i18n/pt.json';
import jaRes from '../i18n/ja.json';
import koRes from '../i18n/ko.json';
import thRes from '../i18n/th.json';

type Language = 'en' | 'zh-TW' | 'zh-CN' | 'da' | 'de' | 'es' | 'fr' | 'it' | 'nl' | 'pt' | 'ja' | 'ko' | 'th';
type TranslationValue = string | Record<string, any>;
type Translations = Record<string, TranslationValue>;

const translations: Record<Language, Translations> = {
  'en': {},
  'zh-TW': {},
  'zh-CN': {},
  'da': {},
  'de': {},
  'es': {},
  'fr': {},
  'it': {},
  'nl': {},
  'pt': {},
  'ja': {},
  'ko': {},
  'th': {}
};

let translationsLoaded = false;

const loadTranslations = () => {
  if (translationsLoaded) return;

  try {
    translations.en = enRes;
    translations['zh-TW'] = zhTWRes;
    translations['zh-CN'] = zhCNRes;
    translations.da = daRes;
    translations.de = deRes;
    translations.es = esRes;
    translations.fr = frRes;
    translations.it = itRes;
    translations.nl = nlRes;
    translations.pt = ptRes;
    translations.ja = jaRes;
    translations.ko = koRes;
    translations.th = thRes;

    translationsLoaded = true;
  } catch (error) {
    console.error('Failed to load translations:', error);
    // Fallback to empty objects if loading fails
    translationsLoaded = true;
  }
};

loadTranslations();

export const useTranslation = (language: string = 'en') => {
  // Normalize language code
  const normalizedLang = normalizeLanguage(language);

  const t = (key: string, params?: Record<string, string | number>): string => {
    // Support nested keys with dot notation
    const keys = key.split('.');
    let translation: any = translations[normalizedLang] || translations.en;

    // Navigate through nested objects
    for (const k of keys) {
      translation = translation?.[k];
      if (translation === undefined) {
        // Try fallback to English
        translation = translations.en;
        for (const k2 of keys) {
          translation = translation?.[k2];
          if (translation === undefined) break;
        }
        break;
      }
    }

    // If no translation found, return the key
    if (translation === undefined || typeof translation !== 'string') {
      return key;
    }

    // Replace parameters in the translation
    if (params) {
      Object.entries(params).forEach(([paramKey, value]) => {
        translation = translation.replace(new RegExp(`\\{${paramKey}\\}`, 'g'), String(value));
      });
    }

    return translation;
  };

  return { t };
};

const normalizeLanguage = (language: string): Language => {
  const lang = language.toLowerCase();

  if (lang.includes('zh')) {
    if (lang.includes('tw') || lang.includes('hk') || lang.includes('hant')) {
      return 'zh-TW';
    } else if (lang.includes('cn') || lang.includes('hans')) {
      return 'zh-CN';
    }
    // Default to Traditional Chinese for other Chinese variants
    return 'zh-TW';
  }

  if (lang.startsWith('da')) return 'da';
  if (lang.startsWith('de')) return 'de';
  if (lang.startsWith('es')) return 'es';
  if (lang.startsWith('fr')) return 'fr';
  if (lang.startsWith('it')) return 'it';
  if (lang.startsWith('nl')) return 'nl';
  if (lang.startsWith('pt')) return 'pt';
  if (lang.startsWith('ja')) return 'ja';
  if (lang.startsWith('ko')) return 'ko';
  if (lang.startsWith('th')) return 'th';

  return 'en';
};

export default useTranslation;
